import { useParams, Link } from "react-router-dom";
import Footer from "../Footer";
import Navbar from "../Navbar";
import notesData from "./notesData";
import "./NotesPage.css";

/* ─────────────────────────────────────────────
   Reusable components
───────────────────────────────────────────── */

const LearningOutcomeBanner = ({ text }) => (
  <div className="notes-outcome-banner">
    <span className="notes-outcome-icon">🎯</span>
    <div>
      <p className="notes-outcome-label">Learning Outcome</p>
      <p className="notes-outcome-text">{text}</p>
    </div>
  </div>
);

/** Card grid for antonym "types" */
const TypeCard = ({ card }) => (
  <div className="notes-type-card" style={{ borderTopColor: card.color }}>
    <div className="notes-type-card-header" style={{ color: card.color }}>
      <span className="notes-type-icon">{card.icon}</span>
      <h3 className="notes-type-title">{card.title}</h3>
    </div>
    <p className="notes-type-body">{card.body}</p>
    {card.examples && card.examples.length > 0 && (
      <div className="notes-examples-box">
        <p className="notes-examples-label">Examples:</p>
        <ul className="notes-examples-list">
          {card.examples.map((ex, i) => (
            <li key={i} className="notes-example-item">
              <span className="notes-example-dot" style={{ background: card.color }} />
              {ex}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

/** Numbered tips */
const TipsList = ({ tips }) => (
  <ul className="notes-tips-list">
    {tips && tips.map((tip, i) => (
      <li key={i} className="notes-tip-item">
        <span className="notes-tip-num">{i + 1}</span>
        <span>{tip}</span>
      </li>
    ))}
  </ul>
);

/** Bullet list */
const BulletList = ({ bullets, color }) => (
  <ul className="notes-bullet-list">
    {bullets && bullets.map((b, i) => (
      <li key={i} className="notes-bullet-item">
        <span className="notes-bullet-dot" style={{ background: color || "#ff820f" }} />
        <span>{b}</span>
      </li>
    ))}
  </ul>
);

/** Numbered hints with optional sub-bullets */
const HintItems = ({ items, color }) => (
  <div className="notes-hints-list">
    {items && items.map((hint, i) => (
      <div key={i} className="notes-hint-block">
        <div className="notes-hint-row">
          <span className="notes-hint-num" style={{ background: color || "#58B6C5" }}>
            {hint.num}
          </span>
          <p className="notes-hint-text">{hint.text}</p>
        </div>
        {hint.subbullets && hint.subbullets.length > 0 && (
          <ul className="notes-hint-subbullets">
            {hint.subbullets.map((sb, j) => (
              <li key={j} className="notes-hint-subbullet">
                <span className="notes-subbullet-arrow">›</span>
                <span>{sb}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    ))}
  </div>
);

/** Titled list items (Academies style) */
const ListItems = ({ items, intro, color }) => (
  <div className="notes-list-items">
    {intro && <p className="notes-list-intro">{intro}</p>}
    {items && items.map((item, i) => (
      <div key={i} className="notes-list-item-card">
        <div className="notes-list-item-num" style={{ background: color || "#407BFF" }}>
          {i + 1}
        </div>
        <div className="notes-list-item-body">
          <p className="notes-list-item-title">{item.title}</p>
          <p className="notes-list-item-desc">{item.description}</p>
        </div>
      </div>
    ))}
  </div>
);

/** Categorised numbered items (Museums style) */
const Categories = ({ categories, color }) => (
  <div className="notes-categories">
    {categories && categories.map((cat, i) => (
      <div key={i} className="notes-category-block">
        <h4 className="notes-category-name" style={{ color: color || "#EBC33C" }}>
          {cat.name}
        </h4>
        <ol className="notes-category-list">
          {cat.items && cat.items.map((item, j) => (
            <li key={j} className="notes-category-item">
              <span className="notes-category-num" style={{ background: color || "#EBC33C" }}>
                {j + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      </div>
    ))}
  </div>
);

/** Sport terms dictionary (sports-terms section) */
const SportTerms = ({ items, color }) => (
  <div className="notes-sport-terms-grid">
    {items && items.map((item, i) => (
      <div key={i} className="notes-sport-term-card">
        <p className="notes-sport-name" style={{ color: color || "#407BFF" }}>
          {item.sport}
        </p>
        <p className="notes-sport-terms">{item.terms}</p>
      </div>
    ))}
  </div>
);

/** Data table (instruments, dance, books) */
const DataTable = ({ table, color }) => (
  <div className="notes-table-wrapper">
    <table className="notes-table">
      <thead>
        <tr>
          {table.headers && table.headers.map((h, i) => (
            <th key={i} style={{ background: color || "#407BFF" }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows && table.rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? "notes-table-row-even" : ""}>
            {row && row.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const KeyTakeaways = ({ items }) => (
  <div className="notes-takeaways-box">
    <h2 className="notes-takeaways-heading">
      <span>⚡</span> Key Takeaways
    </h2>
    <ul className="notes-takeaways-list">
      {items && items.map((item, i) => (
        <li key={i} className="notes-takeaway-item">
          <span className="notes-takeaway-check">✓</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

/* ─────────────────────────────────────────────
   Section renderer  –  picks the right layout
───────────────────────────────────────────── */
const Section = ({ section }) => (
  <div className="notes-section">
    <h2 className="notes-section-heading">
      <span
        className="notes-section-bar"
        style={{
          background: `linear-gradient(180deg, ${section.color || "#ff820f"}, ${section.color || "#ffc26b"}88)`,
        }}
      />
      {section.icon && <span className="notes-section-icon">{section.icon}</span>}
      {section.heading}
    </h2>

    {/* 1. Card grid */}
    {section.subsections && (
      <div className="notes-cards-grid">
        {section.subsections.map((card, i) => (
          <TypeCard key={i} card={card} />
        ))}
      </div>
    )}

    {/* 2. Body paragraph */}
    {section.body && <p className="notes-section-body">{section.body}</p>}

    {/* 3. Bullet list */}
    {section.bullets && (
      <BulletList bullets={section.bullets} color={section.color} />
    )}

    {/* 4. Numbered tips */}
    {section.tips && <TipsList tips={section.tips} />}

    {/* 5. Numbered hints with sub-bullets */}
    {section.hintItems && (
      <HintItems items={section.hintItems} color={section.color} />
    )}

    {/* 6. Titled list items (Akademies) */}
    {section.listItems && (
      <ListItems items={section.listItems} intro={section.intro} color={section.color} />
    )}

    {/* 7. Categorised lists (Museums) */}
    {section.categories && (
      <Categories categories={section.categories} color={section.color} />
    )}

    {/* 8. Data table (Instruments / Dance / Books) */}
    {section.table && (
      <DataTable table={section.table} color={section.color} />
    )}

    {/* 9. Sport terms dictionary */}
    {section.sportTerms && (
      <SportTerms items={section.sportTerms} color={section.color} />
    )}
  </div>
);

/* ─────────────────────────────────────────────
   NotesContent
───────────────────────────────────────────── */
const NotesContent = ({ note }) => (
  <div className="notes-content-wrapper">
    <div className="notes-header">
      <span className="notes-subject-badge">{note.subject}</span>
      <h1 className="notes-main-title">{note.title}</h1>
    </div>

    {note.learningOutcome && <LearningOutcomeBanner text={note.learningOutcome} />}

    {note.introduction && (
      <div className="notes-intro-block">
        <p className="notes-intro-text">{note.introduction}</p>
      </div>
    )}

    {note.sections &&
      note.sections.map((section) => (
        <Section key={section.id} section={section} />
      ))}

    {note.keyTakeaways && note.keyTakeaways.length > 0 && (
      <KeyTakeaways items={note.keyTakeaways} />
    )}
  </div>
);

/* ─────────────────────────────────────────────
   ComingSoon
───────────────────────────────────────────── */
const ComingSoon = ({ displaySubTopic, displayTopic, displayCategory }) => (
  <div className="notes-coming-soon">
    <div className="notes-coming-soon-inner">
      <div className="notes-coming-icon">📝</div>
      <h1 className="notes-coming-title">Coming Soon</h1>
      <p className="notes-coming-desc">
        {displaySubTopic ? (
          <>
            Notes for{" "}
            <span className="notes-coming-highlight">{displaySubTopic}</span>
            {displayCategory && (
              <> ({displayTopic} – {displayCategory})</>
            )}
            {!displayCategory && displayTopic && <> ({displayTopic})</>} are
            under development.
          </>
        ) : (
          "Notes for this subject are under development."
        )}
      </p>
      <p className="notes-coming-sub">Check back soon — we update regularly!</p>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   Helper: look up a note from notesData
   Handles both 2-param and 3-param URLs
───────────────────────────────────────────── */
function lookupNote(topic, testCategory, subTopic) {
  const topicData = notesData[topic] ?? {};

  if (testCategory && subTopic) {
    // 3-param route: notesData[topic][testCategory][subTopic]
    const catData =
      topicData[testCategory] ??
      topicData[testCategory.replace(/_/g, " ")] ??
      {};
    return (
      catData[subTopic] ??
      catData[subTopic.replace(/_/g, " ")] ??
      null
    );
  }

  // 2-param route: notesData[topic][subTopic]
  const key = subTopic ?? "";
  return topicData[key] ?? topicData[key.replace(/_/g, " ")] ?? null;
}

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
function NotesPage() {
  const { topic, testCategory, subTopic } = useParams();

  const displayTopic    = topic?.replace(/_/g, " ");
  const displayCategory = testCategory?.replace(/_/g, " ");
  const displaySubTopic = subTopic?.replace(/_/g, " ");

  const note = lookupNote(topic, testCategory, subTopic);

  return (
    <div className="overflow-x-hidden">
      <Navbar />

      {/* Breadcrumb */}
      <div className="notes-breadcrumb">
        <Link to="/courses" className="notes-breadcrumb-link">Courses</Link>
        <span className="notes-breadcrumb-sep">›</span>
        <Link to={`/courses/${topic}?tab=notes`} className="notes-breadcrumb-link">{displayTopic}</Link>
        {displayCategory && (
          <>
            <span className="notes-breadcrumb-sep">›</span>
            <span className="notes-breadcrumb-text">{displayCategory}</span>
          </>
        )}
        {displaySubTopic && (
          <>
            <span className="notes-breadcrumb-sep">›</span>
            <span className="notes-breadcrumb-active">{displaySubTopic}</span>
          </>
        )}
      </div>

      <div className="notes-page-container">
        {note ? (
          <NotesContent note={note} />
        ) : (
          <ComingSoon
            displaySubTopic={displaySubTopic}
            displayTopic={displayTopic}
            displayCategory={displayCategory}
          />
        )}
      </div>

      <Footer />
    </div>
  );
}

export default NotesPage;